
import { useState } from 'react';

interface OpeningBalanceModalProps {
  onConfirm: (amount: number, note: string, denominations?: CashDenominations) => void;
  onCancel: () => void;
  cashierName: string;
  storeName: string;
  lane: string;
  maxAmount?: number;
  supervisorThreshold?: number;
}

interface CashDenominations {
  bills: {
    hundred: number;
    fifty: number;
    twenty: number;
    ten: number;
    five: number;
    two: number;
    one: number;
  };
  coins: {
    dollar: number;
    quarter: number;
    dime: number;
    nickel: number;
    penny: number;
  };
}

export default function OpeningBalanceModal({
  onConfirm,
  onCancel,
  cashierName,
  storeName,
  lane,
  maxAmount = 5000,
  supervisorThreshold = 500,
}: OpeningBalanceModalProps) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [showDenominations, setShowDenominations] = useState(false);
  const [denominations, setDenominations] = useState<CashDenominations>({
    bills: {
      hundred: 0,
      fifty: 0,
      twenty: 0,
      ten: 0,
      five: 0,
      two: 0,
      one: 0,
    },
    coins: {
      dollar: 0,
      quarter: 0,
      dime: 0,
      nickel: 0,
      penny: 0,
    },
  });
  const [error, setError] = useState('');
  const [showSupervisorWarning, setShowSupervisorWarning] = useState(false);

  const calculateDenominationTotal = () => {
    const billsTotal =
      denominations.bills.hundred * 100 +
      denominations.bills.fifty * 50 +
      denominations.bills.twenty * 20 +
      denominations.bills.ten * 10 +
      denominations.bills.five * 5 +
      denominations.bills.two * 2 +
      denominations.bills.one * 1;

    const coinsTotal =
      denominations.coins.dollar * 1 +
      denominations.coins.quarter * 0.25 +
      denominations.coins.dime * 0.1 +
      denominations.coins.nickel * 0.05 +
      denominations.coins.penny * 0.01;

    return billsTotal + coinsTotal;
  };

  const handleAmountChange = (value: string) => {
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      setError('');
      
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > supervisorThreshold) {
        setShowSupervisorWarning(true);
      } else {
        setShowSupervisorWarning(false);
      }
    }
  };

  const handleDenominationChange = (type: 'bills' | 'coins', key: string, value: string) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= 0) {
      setDenominations({
        ...denominations,
        [type]: {
          ...denominations[type],
          [key]: numValue,
        },
      });
    }
  };

  const syncDenominationsToAmount = () => {
    const total = calculateDenominationTotal();
    setAmount(total.toFixed(2));
    
    if (total > supervisorThreshold) {
      setShowSupervisorWarning(true);
    } else {
      setShowSupervisorWarning(false);
    }
  };

  const handleConfirm = () => {
    const numAmount = parseFloat(amount);

    if (!amount || isNaN(numAmount)) {
      setError('Please enter a valid opening balance amount');
      return;
    }

    if (numAmount < 0) {
      setError('Opening balance cannot be negative');
      return;
    }

    if (numAmount > maxAmount) {
      setError(`Opening balance cannot exceed $${maxAmount.toFixed(2)}`);
      return;
    }

    // If using denominations, verify they match
    if (showDenominations) {
      const denominationTotal = calculateDenominationTotal();
      if (Math.abs(denominationTotal - numAmount) > 0.01) {
        setError('Denomination breakdown does not match the entered amount');
        return;
      }
    }

    onConfirm(numAmount, note, showDenominations ? denominations : undefined);
  };

  const handleCancel = () => {
    if (confirm('Canceling will log you out. Are you sure?')) {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-safe-2-line text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Set Opening Balance</h2>
              <p className="text-teal-100 text-sm">Start your shift with initial cash amount</p>
            </div>
          </div>

          <div className="bg-white/10 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-teal-100">Cashier:</span>
              <span className="font-semibold">{cashierName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-teal-100">Store:</span>
              <span className="font-semibold">{storeName}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-teal-100">Lane:</span>
              <span className="font-semibold">{lane}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-teal-100">Time:</span>
              <span className="font-semibold">{new Date().toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <i className="ri-error-warning-line text-xl text-amber-600 flex-shrink-0 mt-0.5"></i>
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Important Notice</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Opening Balance can only be set once per shift</li>
                <li>This amount cannot be edited after confirmation</li>
                <li>All cash transactions will be tracked against this balance</li>
                <li>You must count and verify physical cash before entering</li>
              </ul>
            </div>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Opening Cash Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-3xl font-bold text-gray-400">$</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0.00"
                className="w-full pl-12 pr-4 py-4 text-3xl font-bold border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-right"
                autoFocus
                disabled={showDenominations}
              />
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </p>
            )}
            {showSupervisorWarning && !error && (
              <p className="mt-2 text-sm text-amber-600 flex items-center gap-2">
                <i className="ri-shield-user-line"></i>
                Amount exceeds ${supervisorThreshold.toFixed(2)} - Supervisor approval required
              </p>
            )}
          </div>

          {/* Denomination Breakdown Toggle */}
          <div>
            <button
              onClick={() => setShowDenominations(!showDenominations)}
              className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
            >
              <i className={`ri-${showDenominations ? 'checkbox' : 'checkbox-blank'}-line text-lg`}></i>
              Use cash denomination breakdown
            </button>
          </div>

          {/* Denomination Breakdown */}
          {showDenominations && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Cash Denomination Breakdown</h3>
                <button
                  onClick={syncDenominationsToAmount}
                  className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white text-xs rounded font-medium cursor-pointer whitespace-nowrap"
                >
                  Calculate Total
                </button>
              </div>

              {/* Bills */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Bills</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'hundred', label: '$100', value: 100 },
                    { key: 'fifty', label: '$50', value: 50 },
                    { key: 'twenty', label: '$20', value: 20 },
                    { key: 'ten', label: '$10', value: 10 },
                    { key: 'five', label: '$5', value: 5 },
                    { key: 'two', label: '$2', value: 2 },
                    { key: 'one', label: '$1', value: 1 },
                  ].map((bill) => (
                    <div key={bill.key} className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-12">{bill.label}</label>
                      <input
                        type="number"
                        min="0"
                        value={denominations.bills[bill.key as keyof typeof denominations.bills]}
                        onChange={(e) => handleDenominationChange('bills', bill.key, e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:border-teal-500 focus:outline-none"
                      />
                      <span className="text-xs text-gray-500 w-16 text-right">
                        ${(denominations.bills[bill.key as keyof typeof denominations.bills] * bill.value).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coins */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Coins</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'dollar', label: '$1', value: 1 },
                    { key: 'quarter', label: '25¢', value: 0.25 },
                    { key: 'dime', label: '10¢', value: 0.1 },
                    { key: 'nickel', label: '5¢', value: 0.05 },
                    { key: 'penny', label: '1¢', value: 0.01 },
                  ].map((coin) => (
                    <div key={coin.key} className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 w-12">{coin.label}</label>
                      <input
                        type="number"
                        min="0"
                        value={denominations.coins[coin.key as keyof typeof denominations.coins]}
                        onChange={(e) => handleDenominationChange('coins', coin.key, e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:border-teal-500 focus:outline-none"
                      />
                      <span className="text-xs text-gray-500 w-16 text-right">
                        ${(denominations.coins[coin.key as keyof typeof denominations.coins] * coin.value).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Denomination Total */}
              <div className="pt-3 border-t border-gray-300">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">Denomination Total:</span>
                  <span className="text-xl font-bold text-teal-600">${calculateDenominationTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Note (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Example: Change float, Opening cash from safe..."
              rows={3}
              maxLength={200}
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none resize-none"
            />
            <p className="mt-1 text-xs text-gray-500">{note.length}/200 characters</p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 space-y-3">
          <button
            onClick={handleConfirm}
            disabled={!amount || parseFloat(amount) < 0}
            className="w-full px-6 py-4 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold text-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-check-line text-xl"></i>
            Confirm Opening Balance
          </button>
          <button
            onClick={handleCancel}
            className="w-full px-6 py-3 bg-white hover:bg-gray-100 border-2 border-gray-300 text-gray-700 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancel &amp; Logout
          </button>
        </div>
      </div>
    </div>
  );
}
